/**
 * Decision 0134 addendum: existing leads and area agents get 0133 §1's wider authority, but only where their stored
 * policy is still an untouched default. A policy record that equals, rule for rule, one of the historical `lead` or
 * `role` presets (any version since the first commit, v1 documents as the read-time upcaster reads them) gets the
 * 0134 change applied to that document and nothing else; a policy someone edited is left alone.
 *
 * The 0134 change, applied to the matched document:
 * - lead: `change_kpi_target`, `define_kpi`, `hire_agent`, `create_area`, `update_area`, `update_agent` leave
 *   `propose`/`never` and become plain `allow` rules (appended in the preset's order);
 * - role: `define_kpi` leaves `propose`/`never` and becomes `allow` under `own_area`.
 * Nothing else moves: a pre-0084 default keeps having no `ready_item` rule (0084 left that a person's choice), and the
 * forced nevers are never touched. Every migrated document is the current preset or a pre-0084 default plus the
 * 0134 change, and neither equals a historical default, so a second pass finds nothing.
 */
import { fromV1PolicyDocument, type V1 } from "@minga/protocol"
import { FORCED_NEVER } from "./policy"
import type { Capability, PolicyDocument, PolicyRule, ProjectState, RuleConstraints } from "./types"

type V1Document = typeof V1.PolicyDocument.Type

const rule = (capability: Capability, when?: RuleConstraints): PolicyRule =>
  when ? { capability, when } : { capability }

const FORCED_V1 = ["edit_policy", "grant_integration", "outward_action"] as const

// ---- historical presets (git history of packages/core/src/policy.ts), frozen ----

/** v1 lead, 2551dfe (first commit, before "no limits at first"). */
const V1_LEAD_2551DFE: V1Document = {
  allow: [
    { capability: "create_initiative", when: { under: "existing_goal", maxActive: 5 } },
    { capability: "update_initiative" },
    { capability: "start_initiative" },
    { capability: "abandon_initiative", when: { ifWorkDone: false } },
    { capability: "create_task" },
    { capability: "update_task" },
    { capability: "cancel_task" },
    { capability: "reprioritize" },
    { capability: "add_dependency" },
    { capability: "remove_dependency" },
    { capability: "spawn_workers", when: { maxConcurrent: 6 } },
    { capability: "assign", when: { to: "role_agents" } },
    { capability: "propose_memory" },
    { capability: "curate_memory" },
  ],
  propose: [
    { capability: "create_goal" },
    { capability: "update_goal" },
    { capability: "change_kpi_target" },
    { capability: "define_kpi" },
    { capability: "hire_agent" },
    { capability: "create_area" },
    { capability: "update_area" },
    { capability: "update_agent" },
    { capability: "abandon_initiative", when: { ifWorkDone: true } },
    { capability: "revise_contract" },
  ],
  never: [...FORCED_V1],
}

/** v1 lead, 6115752 (decision 0018, permissive). a8f0f4c added `complete_task`, which the 0032 read-time backfill adds anyway. */
const V1_LEAD_6115752: V1Document = {
  allow: [
    { capability: "create_goal" },
    { capability: "update_goal" },
    { capability: "create_initiative" },
    { capability: "update_initiative" },
    { capability: "start_initiative" },
    { capability: "abandon_initiative" },
    { capability: "create_task" },
    { capability: "update_task" },
    { capability: "revise_contract" },
    { capability: "cancel_task" },
    { capability: "reprioritize" },
    { capability: "add_dependency" },
    { capability: "remove_dependency" },
    { capability: "spawn_workers" },
    { capability: "assign" },
    { capability: "propose_memory" },
    { capability: "curate_memory" },
  ],
  propose: [
    { capability: "change_kpi_target" },
    { capability: "define_kpi" },
    { capability: "hire_agent" },
    { capability: "create_area" },
    { capability: "update_area" },
    { capability: "update_agent" },
  ],
  never: [...FORCED_V1],
}

/** v1 lead, a8f0f4c (decision 0032): `complete_task` after `cancel_task`. */
const V1_LEAD_A8F0F4C: V1Document = {
  ...V1_LEAD_6115752,
  allow: V1_LEAD_6115752.allow.flatMap((r) =>
    r.capability === "cancel_task" ? [r, { capability: "complete_task" as const }] : [r],
  ),
}

const V1_ROLE_NEVER = [
  "create_goal",
  "change_kpi_target",
  "define_kpi",
  "hire_agent",
  "create_area",
  "update_area",
  "update_agent",
  "curate_memory",
  ...FORCED_V1,
] as const

/** v1 role, 2551dfe. */
const V1_ROLE_2551DFE: V1Document = {
  allow: [
    { capability: "create_task", when: { under: "own_area" } },
    { capability: "update_task", when: { under: "own_task" } },
    { capability: "cancel_task", when: { under: "own_task" } },
    { capability: "add_dependency", when: { under: "own_area" } },
    { capability: "reprioritize", when: { under: "own_area" } },
    { capability: "spawn_workers", when: { maxConcurrent: 3 } },
    { capability: "propose_memory" },
  ],
  propose: [
    { capability: "create_initiative" },
    { capability: "update_initiative" },
    { capability: "start_initiative" },
    { capability: "abandon_initiative" },
    { capability: "assign" },
    { capability: "revise_contract" },
  ],
  never: [...V1_ROLE_NEVER],
}

/** v1 role, 6115752: no worker cap. */
const V1_ROLE_6115752: V1Document = {
  ...V1_ROLE_2551DFE,
  allow: V1_ROLE_2551DFE.allow.map((r) =>
    r.capability === "spawn_workers" ? { capability: "spawn_workers" as const } : r,
  ),
}

/** v1 role, a8f0f4c (decision 0032): `complete_task` under `own_task` after `cancel_task`. */
const V1_ROLE_A8F0F4C: V1Document = {
  ...V1_ROLE_6115752,
  allow: V1_ROLE_6115752.allow.flatMap((r) =>
    r.capability === "cancel_task"
      ? [r, { capability: "complete_task" as const, when: { under: "own_task" as const } }]
      : [r],
  ),
}

/** v2 lead, 42e7db5 (decision 0037, the work-model cutover). */
const LEAD_42E7DB5: PolicyDocument = {
  allow: [
    rule("create_goal"),
    rule("update_goal"),
    rule("create_item", { level: "top" }),
    rule("update_item", { level: "top" }),
    rule("complete_item", { level: "top" }),
    rule("assign", { level: "top" }),
    rule("start_item"),
    rule("cancel_item", { level: "top" }),
    rule("create_item", { level: "child" }),
    rule("update_item", { level: "child" }),
    rule("revise_checks"),
    rule("cancel_item", { level: "child" }),
    rule("complete_item", { level: "child" }),
    rule("reprioritize"),
    rule("add_dependency"),
    rule("remove_dependency"),
    rule("spawn_workers"),
    rule("assign", { level: "child" }),
    rule("propose_memory"),
    rule("curate_memory"),
  ],
  propose: [
    rule("change_kpi_target"),
    rule("define_kpi"),
    rule("hire_agent"),
    rule("create_area"),
    rule("update_area"),
    rule("update_agent"),
  ],
  never: [...FORCED_NEVER],
}

/** v2 lead, 05cb46f (decision 0084): the Ready gate, `ready_item` allowed. The last lead default before 0134. */
const LEAD_05CB46F: PolicyDocument = { ...LEAD_42E7DB5, allow: [...LEAD_42E7DB5.allow, rule("ready_item")] }

/** v2 role, 42e7db5. */
const ROLE_42E7DB5: PolicyDocument = {
  allow: [
    rule("create_item", { under: "own_area", level: "child" }),
    rule("update_item", { under: "own_item", level: "child" }),
    rule("cancel_item", { under: "own_item", level: "child" }),
    rule("complete_item", { under: "own_item", level: "child" }),
    rule("add_dependency", { under: "own_area" }),
    rule("reprioritize", { under: "own_area" }),
    rule("spawn_workers"),
    rule("propose_memory"),
  ],
  propose: [
    rule("create_item", { level: "top" }),
    rule("update_item", { level: "top" }),
    rule("complete_item", { level: "top" }),
    rule("assign", { level: "top" }),
    rule("start_item"),
    rule("cancel_item", { level: "top" }),
    rule("assign", { level: "child" }),
    rule("revise_checks"),
  ],
  never: [...V1_ROLE_NEVER],
}

/** v2 role, 05cb46f (decision 0084): `ready_item` mirrors its create rules. */
const ROLE_05CB46F: PolicyDocument = {
  allow: [...ROLE_42E7DB5.allow, rule("ready_item", { under: "own_area", level: "child" })],
  propose: [
    ...ROLE_42E7DB5.propose.slice(0, 1),
    rule("ready_item", { level: "top" }),
    ...ROLE_42E7DB5.propose.slice(1),
  ],
  never: ROLE_42E7DB5.never,
}

/** v2 role, aa9254a (decision 0097): `define_kpi` from `never` to `propose`. The last role default before 0134. */
const ROLE_AA9254A: PolicyDocument = {
  allow: ROLE_05CB46F.allow,
  propose: [...ROLE_05CB46F.propose, rule("define_kpi")],
  never: ROLE_05CB46F.never.filter((c) => c !== "define_kpi"),
}

/** Every earlier `lead` preset, as stored records read today (v1 through the upcaster). */
export const HISTORICAL_LEAD_PRESETS: ReadonlyArray<PolicyDocument> = [
  fromV1PolicyDocument(V1_LEAD_2551DFE),
  fromV1PolicyDocument(V1_LEAD_6115752),
  fromV1PolicyDocument(V1_LEAD_A8F0F4C),
  LEAD_42E7DB5,
  LEAD_05CB46F,
]

/** Every earlier `role` preset, as stored records read today (v1 through the upcaster). */
export const HISTORICAL_ROLE_PRESETS: ReadonlyArray<PolicyDocument> = [
  fromV1PolicyDocument(V1_ROLE_2551DFE),
  fromV1PolicyDocument(V1_ROLE_6115752),
  fromV1PolicyDocument(V1_ROLE_A8F0F4C),
  ROLE_42E7DB5,
  ROLE_05CB46F,
  ROLE_AA9254A,
]

// ---- matching and the 0134 change ----

/** A rule or capability as a key independent of property order. */
const ruleKey = (r: PolicyRule): string => {
  const w = (r.when ?? {}) as Record<string, unknown>
  const when = Object.keys(w)
    .filter((k) => w[k] !== undefined)
    .sort()
    .map((k) => `${k}=${JSON.stringify(w[k])}`)
    .join(",")
  return `${r.capability}{${when}}`
}

/** A document's rules as sorted multisets: order inside a section never changes a verdict. */
const canonical = (d: PolicyDocument): string =>
  JSON.stringify([d.allow.map(ruleKey).sort(), d.propose.map(ruleKey).sort(), [...d.never].sort()])

const LEAD_DEFAULTS = new Set(HISTORICAL_LEAD_PRESETS.map(canonical))
const ROLE_DEFAULTS = new Set(HISTORICAL_ROLE_PRESETS.map(canonical))

/** Decision 0133 §1: the routine team matters the lead now decides (in the lead preset's order). */
export const LEAD_TEAM_CAPABILITIES: ReadonlyArray<Capability> = [
  "change_kpi_target",
  "define_kpi",
  "hire_agent",
  "create_area",
  "update_area",
  "update_agent",
]

const moveToAllow = (
  d: PolicyDocument,
  caps: ReadonlyArray<Capability>,
  rules: ReadonlyArray<PolicyRule>,
): PolicyDocument => ({
  allow: [...d.allow.filter((r) => !caps.includes(r.capability)), ...rules],
  propose: d.propose.filter((r) => !caps.includes(r.capability)),
  never: d.never.filter((c) => !caps.includes(c)),
})

/**
 * The document a stored policy moves to under the 0134 preset upgrade, or null when it is not an untouched earlier
 * default (customised, already current, or not a lead or role preset).
 */
export const presetUpgrade0134 = (doc: PolicyDocument): PolicyDocument | null => {
  const key = canonical(doc)
  if (LEAD_DEFAULTS.has(key))
    return moveToAllow(
      doc,
      LEAD_TEAM_CAPABILITIES,
      LEAD_TEAM_CAPABILITIES.map((c) => rule(c)),
    )
  if (ROLE_DEFAULTS.has(key))
    return moveToAllow(doc, ["define_kpi"], [rule("define_kpi", { under: "own_area" })])
  return null
}

/** The agents' policies the 0134 preset upgrade would revise, with their new documents. */
export const presetUpgrades0134 = (
  s: ProjectState,
): ReadonlyArray<{
  readonly policyId: ProjectState["policies"][number]["id"]
  readonly document: PolicyDocument
}> =>
  s.policies.flatMap((p) => {
    if (p.subject._tag !== "agent") return []
    const document = presetUpgrade0134(p.document)
    return document ? [{ policyId: p.id, document }] : []
  })
